import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import React from 'react';

interface CustomCheckboxProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

export default function CustomCheckbox({ 
  checked = false, 
  onChange,
  disabled = false,
  size = 'md',
  className = ''
}: CustomCheckboxProps) {
  const sizeClasses = {
    xs: 'w-3.5 h-3.5',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  const iconSizes = {
    xs: 'w-2.5 h-2.5',
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
    lg: 'w-4 h-4'
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled && onChange) {
      onChange(!checked);
    }
  };

  const getCheckboxClasses = () => {
    if (disabled) {
      return 'bg-gray-100 border-gray-300 cursor-not-allowed opacity-50'
    }
    if (checked) {
      return 'bg-gradient-to-br from-[#5B6B8C] to-[#4A5A7A] border-[#5B6B8C] shadow-md shadow-slate-500/20 cursor-pointer'
    }
    return 'bg-white border-[#E4E2DF] hover:border-[#5B6B8C] hover:shadow-sm cursor-pointer'
  }

  return (
    <motion.div
      className={`${sizeClasses[size]} rounded-md border-2 flex items-center justify-center transition-all duration-200 ${getCheckboxClasses()} ${className}`}
      onClick={handleClick}
      whileHover={!disabled ? { scale: 1.1 } : {}}
      whileTap={!disabled ? { scale: 0.95 } : {}}
      role="checkbox"
      aria-checked={checked}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          e.stopPropagation();
          if (onChange) {
            onChange(!checked);
          }
        }
      }}
    >
      {checked && (
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 500, damping: 25 }}
        >
          <Check className={`${iconSizes[size]} text-white`} strokeWidth={3} />
        </motion.div>
      )}
    </motion.div>
  );
}
